/* ============================================================================
 * xnails-search — tag-aware search drawer controller for xnails.se
 * ----------------------------------------------------------------------------
 * NEW custom element under a NEW tag name (<xnails-search>). The Maestrooo
 * Impact bundle's minified `PredictiveSearch` class (assets/theme.js:4014,
 * define-guarded at :4107 for tag `predictive-search`) is NEVER touched: the
 * section no longer emits a <predictive-search> element, so that class simply
 * finds no matching node and stays dormant. No registration race, no monkey-
 * patch, no load-order dependency. Rollback = rename the tag back in Liquid.
 *
 * Why: today the drawer runs `_doPredictiveSearch` -> /search/suggest (title/
 * handle/vendor only, IGNORES `sok-` tags), so "röda"/"fransk" miss Ruby
 * Romance / the French products. This element routes as-you-type to the
 * TAG-AWARE standard endpoint /search?...&type=product,collection&section_id=,
 * which re-renders THIS section's [slot="results"] with full-text + tag-aware
 * products AND a deterministic collections tab (server-side synonym map).
 *
 * Contract preserved from the theme (so all existing CSS/markup still works):
 *   - Host lives inside <form> with input[name="q"]; uses native submit/reset.
 *   - Open shadow root whose ONLY child is one <slot>; its `name` is toggled
 *     between the literal strings idle | loading | results.
 *   - Light-DOM children carry matching slot="idle|loading|results".
 *   - Result HTML swapped by replacing the single [slot="results"] element.
 * ========================================================================== */
(() => {
  'use strict';

  const TAG = 'xnails-search';
  if (customElements.get(TAG)) return; // idempotent registration guard

  const LRU_MAX = 30;
  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Session-scoped LRU: key = full request URL, value = response HTML text.
  const cache = new Map();

  const debounce = (fn, wait) => {
    let t;
    return function (...a) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, a), wait);
    };
  };

  class XnailsSearch extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      // First slot = the panel whose `name` toggles idle|loading|results.
      // Second slot = a PERMANENT status region that is never hidden, so the
      // sr-only live region we mutate always announces (see _announce).
      this.shadowRoot.appendChild(
        document.createRange().createContextualFragment(
          '<slot name="idle"></slot><slot name="status"></slot>'
        )
      );
      this._abort = null;
      this._lastKey = null; // normalized query currently shown / in flight
    }

    connectedCallback() {
      this._form = this.closest('form');
      if (!this._form) return;                 // null-guard the theme omits
      this._input = this._form.elements['q'];
      if (!this._input) return;
      this._status = this.querySelector('[slot="status"]'); // persistent live region

      this._min     = parseInt(this.getAttribute('data-min-length') || '2', 10);
      this._delay   = parseInt(this.getAttribute('data-debounce')   || '280', 10);
      this._type    = this.getAttribute('data-type') || 'product,collection';
      this._section = this.getAttribute('section-id');

      this._onInput  = debounce(this._run.bind(this), this._delay);
      this._onSubmit = (e) => { if (this._input.value.trim() === '') e.preventDefault(); };
      this._onReset  = () => this._clear();

      this._form.addEventListener('submit', this._onSubmit);
      this._form.addEventListener('reset',  this._onReset);
      this._input.addEventListener('input', this._onInput);
    }

    disconnectedCallback() {
      this._abort?.abort();
      this._form?.removeEventListener('submit', this._onSubmit);
      this._form?.removeEventListener('reset',  this._onReset);
      this._input?.removeEventListener('input', this._onInput);
    }

    get _slot() { return this.shadowRoot.firstElementChild; }

    _norm(s) { return s.trim().toLowerCase().replace(/\s+/g, ' '); }

    async _run() {
      const raw = this._input.value;
      const key = this._norm(raw);

      // Min-length gate: the tag-aware /search endpoint is heavier than
      // /suggest, so guard it (the theme had no minimum at all).
      if (key.length < this._min) { this._lastKey = null; return this._clear(); }

      // Coalesce: identical to what is already shown / in flight -> no-op.
      if (key === this._lastKey) return;
      this._lastKey = key;

      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const url =
        `${root}search?q=${encodeURIComponent(raw)}` +
        `&type=${encodeURIComponent(this._type)}` +
        `&options[prefix]=last` +                 // as-you-type prefix on last term
        `&section_id=${encodeURIComponent(this._section)}`;

      // Cache hit -> render instantly, SKIP the skeleton (zero flicker).
      // MUST abort any still-in-flight older fetch first: otherwise a longer
      // query's response (e.g. 'abc' in flight while we backspace to a cached
      // 'ab') lands later, passes its `signal.aborted` guard, and _swap()s its
      // stale results over the fresh cached ones. Aborting restores the
      // single-in-flight invariant every render path relies on.
      if (cache.has(url)) {
        this._abort?.abort();
        this._abort = null;
        const html = cache.get(url);
        cache.delete(url); cache.set(url, html); // bump LRU recency
        this._swap(html);
        await this._to('results');
        return this._announce();
      }

      // Cache miss: abort any prior request, show skeleton, fetch.
      this._abort?.abort();
      this._abort = new AbortController();
      const signal = this._abort.signal;

      try {
        // Kick off the network request BEFORE the loading fade so the ~200ms
        // skeleton reveal overlaps the round-trip instead of serialising ahead
        // of it (the whole point of a skeleton is to show WHILE fetching).
        const fetchP = fetch(url, { signal });
        await this._to('loading');
        const res = await fetchP;
        if (!res.ok) throw new Error('search-' + res.status);
        const html = await res.text();
        // Staleness guard: superseded keystroke (aborted) OR the shown/target
        // query moved on since this closure captured `key`.
        if (signal.aborted || this._lastKey !== key) return;
        cache.set(url, html);
        if (cache.size > LRU_MAX) cache.delete(cache.keys().next().value);
        this._swap(html);
        await this._to('results');
        this._announce();
      } catch (e) {
        if (e.name === 'AbortError' || signal.aborted) return; // awaited + swallowed
        this._lastKey = null;                   // allow retry on next keystroke
        this._renderError();
        await this._to('results');
        this._announce();
      }
    }

    // Parse offline, then perform exactly ONE DOM mutation (replaceWith) so
    // there is no layout thrash and transitions keep targeting slotted nodes.
    _swap(html) {
      const fresh = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[slot="results"]');
      const current = this.querySelector('[slot="results"]');
      if (fresh && current) current.replaceWith(document.importNode(fresh, true));
    }

    _renderError() {
      const current = this.querySelector('[slot="results"]');
      if (!current) return;
      const msg = this.getAttribute('data-error-text') || '';
      // Text-only injection (msg is a Liquid-escaped i18n string) -> no XSS.
      const wrap = document.createElement('div');
      wrap.setAttribute('slot', 'results');
      const box = document.createElement('div');
      box.className = 'empty-state';
      const p = document.createElement('p');
      p.className = 'h6';
      p.textContent = msg;
      box.appendChild(p); wrap.appendChild(box);
      current.replaceWith(wrap);
    }

    // Copy the freshly-rendered result count into the PERSISTENT, always-visible
    // status region so assistive tech reliably announces it. The server-rendered
    // marker lives inside slot="results", which is display:none while the panel
    // shows the loading/idle slot, so mutating it there never fires; this node is
    // never hidden, so setting its text here does announce.
    _announce() {
      if (!this._status) return;
      const results = this.querySelector('[slot="results"]');
      const src = results &&
        (results.querySelector('.search-result-count') ||
         results.querySelector('.empty-state p'));
      this._status.textContent = src ? src.textContent.trim() : '';
    }

    _clear() {
      this._abort?.abort();
      this._lastKey = null;
      if (this._status) this._status.textContent = '';
      this._input?.focus();
      return this._to('idle');
    }

    async _to(name) {
      const slot = this._slot;
      if (slot.getAttribute('name') === name) return;
      if (REDUCED) { slot.setAttribute('name', name); return; }
      const el = (n) => n.nodeType === 1;
      const before = slot.assignedNodes({ flatten: true }).filter(el);
      await Promise.all(before.map((n) =>
        n.animate({ opacity: [1, 0] }, { duration: 100, fill: 'both' }).finished
      )).catch(() => {});
      slot.setAttribute('name', name);
      const after = slot.assignedNodes({ flatten: true }).filter(el);
      await Promise.all(after.map((n) =>
        n.animate(
          { opacity: [0, 1], transform: ['translateY(5px)', 'translateY(0)'] },
          { duration: 100, fill: 'both' }
        ).finished
      )).catch(() => {});
    }
  }

  customElements.define(TAG, XnailsSearch);
})();
